import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtrasado } from "../componentes/atraso.js";
import { Link, useSearchParams } from "react-router";
import {
  agruparPorMateriaEAssunto,
  EXIBICOES_DO_DIAGNOSTICO,
  montarPedidoDeItens,
  NOME_DA_EXIBICAO,
  NOME_DO_PERIODO,
  PERIODOS_DO_DIAGNOSTICO,
  textoDoGabarito,
  textoDoMarcado,
  type DiagnosticoDeErros,
  type ErroDiagnosticado,
  type ExibicaoDoDiagnostico,
  type MateriaComErro,
  type PeriodoDoDiagnostico,
} from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { ExplicacaoDoItem } from "../componentes/ExplicacaoDoItem.js";
import { Icone } from "../componentes/Icone.js";
import { SeletorSegmentado } from "../componentes/Controles.js";
import { usePraticar } from "../estado/praticar.js";
import { IconeAcerto, IconeChevron, IconeErro, IconeExportar } from "../componentes/icones.js";
import { useTrilhaAtiva, useTrilhas } from "../estado/trilhas.js";

/**
 * Diagnóstico de erros — RN-46, tela 11 do SPEC, refeita na Fase 4.
 *
 * **Não é a fila de erradas**, e a diferença é a pergunta que cada uma responde.
 * A fila responde "o que eu estudo agora", ordenada pelo score da RN-45. Esta
 * responde "onde eu erro", e mostra **qual alternativa foi marcada** em cada
 * erro — o dado que estava em `resposta.marcado` desde a F1 sem nunca ter sido
 * mostrado. Saber que se errou a questão 12 não diz nada; saber que se marcou
 * "C" quando a certa era "A" diz qual confusão está acontecendo.
 *
 * **A ordem da tela é a ordem da pergunta**: primeiro os filtros e a saída,
 * depois quanto (os quatro indicadores), depois onde (o ranking de assuntos),
 * depois o quê (o acordeão por matéria). Ela era a lista crua logo abaixo de um
 * parágrafo, sem nenhum número antes — quem abria tinha que ler tudo para saber
 * o tamanho do problema.
 *
 * **Ela fecha o ciclo.** O Markdown de saída é um pedido pronto para colar num
 * modelo de linguagem, e o JSON que voltar entra pela mesma importação de
 * sempre: errar → gerar itens do ponto fraco → importar.
 */

const ehPeriodo = (v: string | null): boolean =>
  v !== null && (PERIODOS_DO_DIAGNOSTICO as readonly string[]).includes(v);
const ehExibicao = (v: string | null): boolean =>
  v !== null && (EXIBICOES_DO_DIAGNOSTICO as readonly string[]).includes(v);

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; dados: DiagnosticoDeErros }
  | { nome: "erro"; mensagem: string };

export function Diagnostico() {
  const trilha = useTrilhaAtiva();
  const { estado: trilhas } = useTrilhas();
  /*
   * Os dois recortes moram na **URL**, como a aba (RN-60) e pela mesma razão:
   * voltar para a tela, recarregar a página ou mandar o link precisam cair no
   * mesmo recorte. Em `useState` eles se perdiam no primeiro passo para fora —
   * e é exatamente o que a Fase 6A pede que não aconteça.
   */
  const [params, setParams] = useSearchParams();
  const periodo = ehPeriodo(params.get("periodo")) ? (params.get("periodo") as PeriodoDoDiagnostico) : "tudo";
  const mostrar = ehExibicao(params.get("mostrar")) ? (params.get("mostrar") as ExibicaoDoDiagnostico) : "todas";

  const trocarRecorte = useCallback(
    (chave: "periodo" | "mostrar", valor: string) => {
      const proximos = new URLSearchParams(params);
      // O padrão não vai para a URL: `?aba=diagnostico` sozinho é o endereço
      // limpo desta tela, e sujá-lo com os padrões faria todo link nascer longo.
      if ((chave === "periodo" && valor === "tudo") || (chave === "mostrar" && valor === "todas")) {
        proximos.delete(chave);
      } else {
        proximos.set(chave, valor);
      }
      // `replace`: trocar de filtro é refinar a mesma tela, não um destino novo.
      // Sem isto o botão de voltar do navegador percorreria cada toque.
      setParams(proximos, { replace: true });
    },
    [params, setParams],
  );
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");
  const [copiado, setCopiado] = useState(false);

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      setEstado({ nome: "pronto", dados: await pedir("diagnostico.erros", { params: { periodo } }) });
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível montar o diagnóstico.",
      });
    }
  }, [periodo]);

  useEffect(() => {
    void carregar();
  }, [carregar, trilha?.id]);

  // Trocar de filtro invalida o "Copiado" da vez anterior.
  useEffect(() => setCopiado(false), [periodo, mostrar]);

  /*
   * **A coluna não pode trocar de largura no meio do carregamento** (Fase 6A).
   *
   * O estado "sem trilha" é coluna de leitura (544px) e o pronto é coluna ampla
   * (1040px). Enquanto as trilhas não chegam, `useTrilhaAtiva` devolve `null` —
   * indistinguível de "não há nenhuma" —, e a tela pintava a estreita para
   * trocar pela ampla um quadro depois. Foi **0,14 de CLS** na medida da fase,
   * e o culpado que a medida apontou foi um link da barra superior: o salto é
   * horizontal, e por isso ele não aparece em captura nenhuma.
   */
  if (trilhas.nome !== "pronto") {
    return (
      <div className="tela tela--ampla tela--painel">
        <div className="tela__corpo" aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <div className="esqueleto esqueleto--titulo" />
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </div>
    );
  }

  if (!trilha) {
    return (
      <div className="tela tela--painel">
        <div className="tela__corpo">
          <section className="estado">
            <h2 className="estado__titulo">Nenhuma trilha ativa</h2>
            <p className="estado__texto">O diagnóstico é de uma trilha por vez.</p>
            <Link className="botao" to="/trilhas">
              Escolher uma trilha
            </Link>
          </section>
        </div>
      </div>
    );
  }

  if (estado.nome === "carregando") {
    return (
      <div className="tela tela--ampla tela--painel">
        <div className="tela__corpo" aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <span className="so-leitor" role="status">
            Montando o diagnóstico
          </span>
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </div>
    );
  }

  if (estado.nome === "erro") {
    return (
      <div className="tela tela--painel">
        <div className="tela__corpo">
          <section className="estado estado--erro" role="alert">
            <h1 className="estado__titulo">Não deu para montar</h1>
            <p className="estado__texto">{estado.mensagem}</p>
            <button className="botao" type="button" onClick={() => void carregar()}>
              Tentar de novo
            </button>
          </section>
        </div>
      </div>
    );
  }

  const d = estado.dados;
  const pedido = montarPedidoDeItens(d);

  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">
        <Filtros
          periodo={periodo}
          mostrar={mostrar}
          aoTrocarPeriodo={(p) => trocarRecorte("periodo", p)}
          aoTrocarExibicao={(e) => trocarRecorte("mostrar", e)}
          copiado={copiado}
          pedido={pedido}
          trilha={d.trilha}
          aoCopiar={setCopiado}
        />

        {d.erros.length === 0 ? (
          <section className="estado">
            <h2 className="estado__titulo">Nenhum erro na janela</h2>
            <p className="estado__texto">
              {periodo === "tudo"
                ? "Você ainda não errou nada nesta trilha."
                : "Nenhum erro nos últimos dias escolhidos. Amplie o período para ver o histórico."}
            </p>
            <Link className="botao" to="/estudar">
              Estudar
            </Link>
          </section>
        ) : (
          <>
            <Indicadores resumo={d.resumo} />
            <div className="diagnostico__colunas">
              <Ranking ranking={d.ranking} erros={d.erros} />
              <section className="diagnostico__por-materia" aria-labelledby="por-materia-titulo">
                <h2 className="t-secao" id="por-materia-titulo">
                  Por matéria
                </h2>
                <ListaPorMateria dados={d} mostrar={mostrar} />
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- filtros */

function Filtros({
  periodo,
  mostrar,
  aoTrocarPeriodo,
  aoTrocarExibicao,
  copiado,
  pedido,
  trilha,
  aoCopiar,
}: {
  periodo: PeriodoDoDiagnostico;
  mostrar: ExibicaoDoDiagnostico;
  aoTrocarPeriodo: (p: PeriodoDoDiagnostico) => void;
  aoTrocarExibicao: (e: ExibicaoDoDiagnostico) => void;
  copiado: boolean;
  pedido: string;
  trilha: string;
  aoCopiar: (v: boolean) => void;
}) {
  /*
   * O download é montado no navegador, a partir do mesmo Markdown que o botão
   * de copiar entrega. Duas saídas do mesmo texto, e não duas versões dele:
   * exportar um arquivo diferente do que se cola no modelo seria a forma mais
   * rápida de os dois divergirem sem ninguém notar.
   */
  function exportar() {
    const blob = new Blob([pedido], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `noesis-erros-${trilha.toLocaleLowerCase("pt-BR").replace(/\s+/g, "-")}-${periodo}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="diagnostico__filtros">
      <div className="diagnostico__recortes">
        <Segmentado
          rotulo="Período"
          opcoes={PERIODOS_DO_DIAGNOSTICO.map((p) => ({ valor: p, rotulo: NOME_DO_PERIODO[p] }))}
          atual={periodo}
          aoEscolher={(v) => aoTrocarPeriodo(v as PeriodoDoDiagnostico)}
        />
        <Segmentado
          rotulo="Mostrar"
          opcoes={EXIBICOES_DO_DIAGNOSTICO.map((e) => ({ valor: e, rotulo: NOME_DA_EXIBICAO[e] }))}
          atual={mostrar}
          aoEscolher={(v) => aoTrocarExibicao(v as ExibicaoDoDiagnostico)}
        />
      </div>

      <div className="diagnostico__acoes">
        <button
          type="button"
          className="botao botao--secundario"
          onClick={() => {
            void navigator.clipboard
              .writeText(pedido)
              .then(() => aoCopiar(true))
              .catch(() => aoCopiar(false));
          }}
        >
          <Icone glifo="copiar" papel="acao" />
          {copiado ? "Copiado" : "Copiar para o Claude"}
        </button>
        <button type="button" className="botao" onClick={exportar}>
          <Icone glifo={IconeExportar} papel="acao" />
          Exportar .md
        </button>
      </div>
    </div>
  );
}

/** Um grupo de pílulas de escolha única. Pílula filtra, aba navega. */
function Segmentado({
  rotulo,
  opcoes,
  atual,
  aoEscolher,
}: {
  rotulo: string;
  opcoes: readonly { valor: string; rotulo: string }[];
  atual: string;
  aoEscolher: (v: string) => void;
}) {
  return (
    <div className="filtros-mao__linha diagnostico__recorte">
      <span className="t-anotacao t-2 filtros-mao__rotulo" aria-hidden="true">
        {rotulo.toLocaleLowerCase("pt-BR")}
      </span>
      <SeletorSegmentado rotulo={rotulo} opcoes={opcoes} valor={atual} aoMudar={aoEscolher} />
    </div>
  );
}

/* ------------------------------------------------------------ indicadores */

function Indicadores({ resumo }: { resumo: DiagnosticoDeErros["resumo"] }) {
  const faixa = [
    { valor: resumo.erradas, rotulo: "Questões erradas", legenda: "errou ao menos uma vez" },
    { valor: resumo.pendentes, rotulo: "Pendentes", legenda: "a última resposta foi erro" },
    { valor: resumo.recuperadas, rotulo: "Recuperadas", legenda: "a última já foi acerto" },
    { valor: resumo.materiasAfetadas, rotulo: "Matérias afetadas", legenda: "com ao menos um erro" },
  ];
  return (
    <section className="diagnostico__resumo" aria-label="Resumo dos erros">
      <div className="diagnostico__faixa">
        {faixa.map((i, n) => (
          <div className="diagnostico__indicador" key={i.rotulo}>
            <p className={`t-numero diagnostico__numero diagnostico__numero--${n}`}>{i.valor}</p>
            <p className="t-corpo t-forte">{i.rotulo.toLocaleLowerCase("pt-BR")}</p>
            <p className="t-ajuda">{i.legenda}</p>
          </div>
        ))}
      </div>
      <p className="t-corpo t-2">
        Para praticar essas questões, use a <Link to="/revisar">fila de erradas</Link>.
      </p>
    </section>
  );
}

/** A barra de duas cores: recuperadas em verde, pendentes em erro. */
function RecuperadasPendentes({ recuperadas, pendentes }: { recuperadas: number; pendentes: number }) {
  const total = Math.max(1, recuperadas + pendentes);
  return (
    <span
      className="barra-dupla"
      style={{ ["--parte-a" as string]: String(recuperadas / total), ["--parte-b" as string]: String(pendentes / total) }}
      aria-hidden="true"
    >
      <span className="barra-dupla__a" />
      <span className="barra-dupla__b" />
    </span>
  );
}

function Ranking({ ranking, erros }: { ranking: DiagnosticoDeErros["ranking"]; erros: DiagnosticoDeErros["erros"] }) {
  const { praticar, ocupado } = usePraticar();
  if (ranking.length === 0) return null;
  // Pendentes e recuperadas por assunto saem da lista de erros, que já vem
  // com o `recuperado` de cada questão.
  const porCaminho = new Map<string, { pendentes: number; recuperadas: number }>();
  for (const e of erros) {
    const k = e.categoria.join(" › ");
    const c = porCaminho.get(k) ?? { pendentes: 0, recuperadas: 0 };
    if (e.recuperado) c.recuperadas += 1;
    else c.pendentes += 1;
    porCaminho.set(k, c);
  }
  return (
    <section className="diagnostico__ranking" aria-labelledby="ranking-titulo">
      <h2 className="t-secao" id="ranking-titulo">
        Onde mais erro
      </h2>
      <ol className="ranking">
        {ranking.map((a, i) => {
          const chave = a.caminho.join(" › ");
          const conta = porCaminho.get(chave) ?? { pendentes: 0, recuperadas: 0 };
          return (
            <li className="ranking__linha" key={chave}>
              <span className="ranking__posicao t-secao">{i + 1}</span>
              <span className="ranking__nome t-corpo">{a.caminho.join(", ")}</span>
              <span className="ranking__questoes t-anotacao-p">
                {a.questoes} {a.questoes === 1 ? "questão" : "questões"} · {a.erros} {a.erros === 1 ? "erro" : "erros"}
                {conta.pendentes > 0 && <span className="so-leitor">, {conta.pendentes} pendentes</span>}
              </span>
              <span className="ranking__acao">
                <button
                  type="button"
                  className="link-mao"
                  disabled={ocupado !== null}
                  onClick={() =>
                    void praticar(chave, { caminho: a.caminho }, { soErradosAntes: true, contexto: "erradas" })
                  }
                >
                  {ocupado === chave ? "montando…" : "praticar"}
                </button>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* --------------------------------------------------------------- acordeão */

function ListaPorMateria({ dados, mostrar }: { dados: DiagnosticoDeErros; mostrar: ExibicaoDoDiagnostico }) {
  const visiveis = useMemo(
    () =>
      dados.erros.filter((e) =>
        mostrar === "pendentes" ? !e.recuperado : mostrar === "recuperadas" ? e.recuperado : true,
      ),
    [dados.erros, mostrar],
  );

  const porMateria = useMemo(() => agruparPorMateriaEAssunto(visiveis, (e) => e.categoria), [visiveis]);

  if (visiveis.length === 0) {
    return (
      <section className="estado">
        <h2 className="estado__titulo">Nada nesta seleção</h2>
        <p className="estado__texto">
          {mostrar === "pendentes"
            ? "Você acertou de novo tudo o que tinha errado na janela."
            : "Nenhuma das questões erradas na janela foi acertada de novo ainda."}
        </p>
      </section>
    );
  }

  return (
    <div className="pilha">
      {porMateria.map((materia) => {
        const agregado = dados.materias.find((m) => m.nome === materia.materia);
        return (
          <Materia
            key={materia.materia ?? "(sem matéria)"}
            nome={materia.materia}
            agregado={agregado}
            assuntos={materia.itens}
          />
        );
      })}
    </div>
  );
}

function Materia({
  nome,
  agregado,
  assuntos,
}: {
  nome: string | null;
  agregado: MateriaComErro | undefined;
  assuntos: { materia: string | null; itens: ErroDiagnosticado[] }[];
}) {
  const [aberta, setAberta] = useState(false);
  const taxa = agregado && agregado.respostas > 0 ? Math.round((agregado.certas / agregado.respostas) * 100) : null;

  return (
    <section className="diagnostico__materia">
      <button
        type="button"
        className="diagnostico__cabecalho-materia"
        aria-expanded={aberta}
        onClick={() => setAberta((v) => !v)}
      >
        <Icone
          glifo={IconeChevron}
          papel="acao"
          tamanho="p"
          className={aberta ? "caderno__chevron caderno__chevron--aberto" : "caderno__chevron"}
        />
        <span className="diagnostico__nome-materia">{nome ?? "Sem matéria"}</span>
        {taxa !== null && (
          <>
            {/*
              A barra não é enfeite: ela é o que faz duas matérias de "8 erros"
              se distinguirem de relance quando uma tem 20 respostas e a outra
              200. `<meter>` não serve aqui — ele traz estilo de agente de
              usuário que não atravessa os dois temas.
            */}
            <BarraDeTaxa taxa={taxa} />
            <span className="t-secao diagnostico__taxa">{taxa}%</span>
          </>
        )}
        <span className="t-anotacao-p diagnostico__contagem">
          {agregado?.erros ?? 0} {(agregado?.erros ?? 0) === 1 ? "erro" : "erros"} em {agregado?.questoes ?? 0}{" "}
          {(agregado?.questoes ?? 0) === 1 ? "questão" : "questões"}
        </span>
      </button>

      {/*
        **Aqui o conteúdo só monta quando abre**, ao contrário do acordeão do
        caderno — e a diferença é o tamanho do que está dentro. Uma matéria traz
        dezenas de questões com explicação renderizada: animar a altura disso faz
        a página crescer alguns milhares de pixels, e o que se vê não é uma
        abertura, é um solavanco. O desvanecer diz a mesma coisa sem mexer no
        tamanho da página.
      */}
      {aberta && (
        <div className="diagnostico__conteudo-materia pilha entra-desvanecendo">
          {assuntos.map((assunto) => (
            <section className="pilha" key={assunto.materia ?? "(direto)"}>
              <h3 className="secundario diagnostico__assunto-titulo">
                {assunto.materia ?? "Direto na matéria"}
              </h3>
              <ul className="pilha">
                {assunto.itens.map((e) => (
                  <CartaoDeErro key={e.itemId} erro={e} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * A barra da taxa de acerto.
 *
 * A largura chega por **custom property escrita via CSSOM**, e não por atributo
 * `style`: a CSP do Noesis é `style-src 'self'` sem `style-src-attr`, então
 * `style={{ width }}` seria bloqueado e a barra nasceria sempre vazia. É o mesmo
 * idioma do cursor fluido do caderno e do menu do seletor múltiplo.
 */
function BarraDeTaxa({ taxa }: { taxa: number }) {
  const cheia = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    cheia.current?.style.setProperty("--taxa", `${taxa}%`);
  }, [taxa]);
  return (
    <span className="diagnostico__barra" aria-hidden="true">
      <span className="diagnostico__barra-cheia" ref={cheia} />
    </span>
  );
}

/**
 * Um erro da lista.
 *
 * **O enunciado é o texto principal**, e não um apoio cinza: ele era
 * `.leitura--apoio`, menor e mais apagado que a explicação logo abaixo — a tela
 * destacava o comentário sobre a questão em vez da questão. Aqui ele é tinta
 * primária, 16px, peso 500, e a explicação vem depois, no tamanho de leitura.
 */
function CartaoDeErro({ erro }: { erro: ErroDiagnosticado }) {
  const marcada = erro.marcacoesErradas[0];
  const certa = textoDoGabarito(erro.tipo, erro.gabarito, erro.alternativas);
  return (
    <li className="bloco pilha diagnostico__item">
      <p className="diagnostico__enunciado">{erro.enunciado}</p>

      <div className="diagnostico__respostas">
        {marcada && (
          <span className="metadado diagnostico__marcada">
            <Icone glifo={IconeErro} papel="estado" tamanho="p" />
            Você marcou: {textoDoMarcado(marcada.marcado, erro.alternativas)}
          </span>
        )}
        {/*
          A linha some no cartão: ele não tem certo objetivo, quem julgou foi o
          usuário. Antes ela aparecia com um rótulo vazio, e "(sem gabarito)"
          num item que nunca teve gabarito parece defeito de importação.
        */}
        {certa && (
          <span className="metadado diagnostico__certa">
            <Icone glifo={IconeAcerto} papel="estado" tamanho="p" />
            Certa: {certa}
          </span>
        )}
      </div>

      <p className="metadado">
        Errou <span className="dado">{erro.erros}</span> de <span className="dado">{erro.tentativas}</span>{" "}
        {erro.tentativas === 1 ? "vez" : "vezes"}
        {erro.recuperado && " · recuperada"}
      </p>

      {erro.explicacao && <ExplicacaoDoItem markdown={erro.explicacao} />}
    </li>
  );
}
